import { useCallback, useEffect, useMemo, useState } from 'react';

import useAllPermissionsQuery from '../../../../../hooks/api/roles/useAllPermissionsQuery';
import type { RolePermission } from '../../../../../../shared/model-typings';
import type { PermissionGroup, PermissionMetadata } from '../../../../../../typings/api';
import type { RoleScope } from '../../../../../../typings/enums';

export type AllPermissionsItem = PermissionMetadata & {
  id: string;
  isChecked: boolean;
};

export type PermissionsByGroup = Record<PermissionGroup, AllPermissionsItem[]>;

type PermissionsEntries = Array<[string, AllPermissionsItem[]]>;

type UseAllPermissionsProps = {
  isReadOnly: boolean;
  scope: RoleScope;
  rolePermissions: RolePermission[];
  onPermissionsChange: (permissions: AllPermissionsItem[]) => void;
};

type UseAllPermissionsState = {
  isLoading: boolean;
  searchQuery: string;
  filteredPermissions: PermissionsByGroup;
  isAllPermissionsChecked: boolean;
  permissionsSelected: {
    all: number;
    hidden: number;
  };
};

type UseAllPermissionsDispatch = {
  onSearchQueryChange: (updatedSearchQuery: string) => void;
  onTogglePermission: (permissionId: string) => void;
  onToggleGroup: (group: PermissionGroup) => void;
  onToggleAll: () => void;
};

type UseAllPermissions = {
  state: UseAllPermissionsState;
  dispatch: UseAllPermissionsDispatch;
  groups: PermissionsEntries;
};

export const permissionIsChecked = (permission: AllPermissionsItem) => permission.isChecked;

const permissionHasScope = (permission: AllPermissionsItem | PermissionMetadata, scope: RoleScope) =>
  permission.scopes.includes(scope);

const permissionMatchesSearchQuery = (permission: AllPermissionsItem, query: string) => {
  if (!query || permission.description.toLowerCase().includes(query.toLowerCase())) {
    return true;
  }
  return false;
};

const makePermissionId = (permission: PermissionMetadata) =>
  `permission-${permission.resourceType}-${permission.action}`;

const isPermissionActive = (
  permission: PermissionMetadata,
  rolePermissions: RolePermission[],
): boolean =>
  rolePermissions.some(
    (rp) => rp.resourceType === permission.resourceType && rp.action === permission.action,
  );

/**
 * Hook for managing permissions selection state in the role editor.
 *
 * Fetches all available permissions from the API and provides state and handlers
 * for selecting/deselecting permissions. Supports filtering by scope (organization
 * vs team) and by search query, with permissions grouped by category.
 *
 * @param props - The hook configuration
 * @param props.scope - The role scope ('organization' or 'team') to filter permissions by
 * @param props.isReadOnly - When true, all toggle handlers become no-ops
 * @param props.rolePermissions - The role's current permissions, used to initialize checked state
 * @param props.onPermissionsChange - Callback fired when the selected permissions change
 *
 * @returns An object containing:
 *   - `state`: Current state including loading, search query, filtered permissions, and selection counts
 *   - `dispatch`: Handlers for search, toggling individual permissions, groups, or all
 *   - `groups`: Permissions grouped by category as [groupName, permissions][] entries
 */
export default function useAllPermissions({
  scope,
  isReadOnly,
  rolePermissions,
  onPermissionsChange,
}: UseAllPermissionsProps): UseAllPermissions {
  const { data: permissionsMetadata, isLoading } = useAllPermissionsQuery();
  const [searchQuery, setSearchQuery] = useState('');
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  // Memoize to avoid creating a new array reference on each render when permissionsMetadata is undefined
  const permissionsList = useMemo(() => permissionsMetadata ?? [], [permissionsMetadata]);

  const allPermissions = useMemo<AllPermissionsItem[]>(() => {
    return permissionsList.map((permission: PermissionMetadata) => ({
      ...permission,
      id: makePermissionId(permission),
      isChecked: checkedIds.has(makePermissionId(permission)),
    }));
  }, [permissionsList, checkedIds]);

  useEffect(() => {
    if (permissionsList.length > 0 && !isInitialized) {
      const initialCheckedIds = new Set<string>();
      permissionsList.forEach((permission: PermissionMetadata) => {
        if (isPermissionActive(permission, rolePermissions)) {
          initialCheckedIds.add(makePermissionId(permission));
        }
      });
      setCheckedIds(initialCheckedIds);
      setIsInitialized(true);
    }
  }, [permissionsList, rolePermissions, isInitialized]);

  // When scope changes, filter out any checked permissions that don't belong to the new scope.
  // We use permissionsList (raw API data) instead of allPermissions to avoid a circular dependency
  // (allPermissions depends on checkedIds, which this effect updates).
  useEffect(() => {
    if (isInitialized && permissionsList.length > 0) {
      setCheckedIds((prev) => {
        const newCheckedIds = new Set<string>();
        prev.forEach((id) => {
          const permission = permissionsList.find((p) => makePermissionId(p) === id);
          if (permission && permissionHasScope(permission, scope)) {
            newCheckedIds.add(id);
          }
        });
        return newCheckedIds;
      });
    }
  }, [scope, isInitialized, permissionsList]);

  const updateCheckedIds = useCallback(
    (newCheckedIds: Set<string>) => {
      setCheckedIds(newCheckedIds);
      const checkedPermissions = allPermissions.filter((p) => newCheckedIds.has(p.id));
      onPermissionsChange(checkedPermissions);
    },
    [allPermissions, onPermissionsChange],
  );

  const currentPermissions = useMemo(
    () => allPermissions.filter((permission) => permissionHasScope(permission, scope)),
    [allPermissions, scope],
  );

  const filteredPermissions = useMemo<PermissionsByGroup>(() => {
    return currentPermissions.reduce((grouped, permission) => {
      if (!permissionMatchesSearchQuery(permission, searchQuery)) {
        return grouped;
      }
      Object.assign(grouped, { [permission.group]: grouped[permission.group] ?? [] });
      grouped[permission.group].push(permission);
      return grouped;
    }, {} as PermissionsByGroup);
  }, [currentPermissions, searchQuery]);

  const groups = useMemo<PermissionsEntries>(
    () => Object.entries(filteredPermissions),
    [filteredPermissions],
  );

  const checkedPermissions = useMemo(
    () => allPermissions.filter(permissionIsChecked),
    [allPermissions],
  );

  const isAllPermissionsChecked = useMemo(
    () => {
      const visible = currentPermissions.filter((p) => permissionMatchesSearchQuery(p, searchQuery));
      return visible.length > 0 && visible.every(permissionIsChecked);
    },
    [currentPermissions, searchQuery],
  );

  const onSearchQueryChange = useCallback((query: string) => setSearchQuery(query), []);

  const onTogglePermission = useCallback(
    (permissionId: string) => {
      if (isReadOnly) {
        return;
      }
      const newCheckedIds = new Set(checkedIds);
      if (newCheckedIds.has(permissionId)) {
        newCheckedIds.delete(permissionId);
      } else {
        newCheckedIds.add(permissionId);
      }
      updateCheckedIds(newCheckedIds);
    },
    [isReadOnly, checkedIds, updateCheckedIds],
  );

  const onToggleGroup = useCallback(
    (group: PermissionGroup) => {
      if (isReadOnly) {
        return;
      }
      const groupPermissions = currentPermissions.filter(
        (p) =>
          p.group === group &&
          permissionHasScope(p, scope) &&
          permissionMatchesSearchQuery(p, searchQuery),
      );
      const isGroupAllChecked = groupPermissions.every(permissionIsChecked);
      const newCheckedIds = new Set(checkedIds);
      groupPermissions.forEach((p) => {
        if (isGroupAllChecked) {
          newCheckedIds.delete(p.id);
        } else {
          newCheckedIds.add(p.id);
        }
      });
      updateCheckedIds(newCheckedIds);
    },
    [isReadOnly, currentPermissions, scope, searchQuery, checkedIds, updateCheckedIds],
  );

  const onToggleAll = useCallback(() => {
    if (isReadOnly) {
      return;
    }
    const visiblePermissions = currentPermissions.filter((p) =>
      permissionMatchesSearchQuery(p, searchQuery),
    );
    const isAllChecked = visiblePermissions.every(permissionIsChecked);
    const newCheckedIds = new Set(checkedIds);
    visiblePermissions.forEach((p) => {
      if (isAllChecked) {
        newCheckedIds.delete(p.id);
      } else {
        newCheckedIds.add(p.id);
      }
    });
    updateCheckedIds(newCheckedIds);
  }, [isReadOnly, currentPermissions, searchQuery, checkedIds, updateCheckedIds]);

  return {
    state: {
      isLoading,
      searchQuery,
      filteredPermissions,
      isAllPermissionsChecked,
      permissionsSelected: {
        all: checkedPermissions.length,
        hidden: checkedPermissions.filter((p) => !permissionMatchesSearchQuery(p, searchQuery))
          .length,
      },
    },
    groups,
    dispatch: {
      onSearchQueryChange,
      onTogglePermission,
      onToggleGroup,
      onToggleAll,
    },
  };
}
